import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coffee } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = await register(name, email, password);
    setLoading(false);
    if (r.ok) navigate("/", { replace: true });
    else setError(r.error);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid md:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-16 order-2 md:order-1">
        <form onSubmit={handle} className="w-full max-w-sm">
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Join the directory</p>
          <h1 className="font-serif text-4xl mt-3">Create your BrewFinder account</h1>
          <p className="text-sm text-[#786B63] mt-2">
            Free forever. No spam. Just better coffee discoveries.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1.5 rounded-sm bg-[#F9F6F0] border-border h-11"
                data-testid="register-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 rounded-sm bg-[#F9F6F0] border-border h-11"
                data-testid="register-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1.5 rounded-sm bg-[#F9F6F0] border-border h-11"
                data-testid="register-password"
              />
              <p className="text-xs text-[#786B63] mt-1">At least 6 characters.</p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-[#C05A46] mt-4" data-testid="register-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="mt-6 w-full rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0] h-11"
          >
            {loading ? "Creating…" : "Create account"}
          </Button>

          <p className="text-sm text-[#786B63] mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#C05A46] hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </form>
      </div>
      <div className="hidden md:block relative overflow-hidden order-1 md:order-2">
        <img
          src="https://images.unsplash.com/photo-1495474472205-cbea1238eb11?q=80&w=1400&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#2C1E16]/70 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-[#F9F6F0]">
          <Coffee className="h-7 w-7 mb-4" strokeWidth={1.5} />
          <p className="font-serif text-3xl leading-tight max-w-md">
            Single-origin discoveries. Saved. Reviewed. Shared.
          </p>
        </div>
      </div>
    </div>
  );
}
